import { useState, useEffect, useRef, useCallback } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import {
  MessageCircle, Plus, Loader2, PhoneCall, Wrench, Bot,
  X, AlertTriangle, CheckCircle2, Upload, File as FileIcon,
  ChevronLeft, ChevronRight, Info, ArrowRight,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type TypeDemande = "information" | "installation" | "sav";

interface PuduRobotMatch {
  id: string;
  robotSn: string;
  robotType: string | null;
  robotName: string | null;
  clientNom?: string | null;
}

interface DoublonChat {
  id: string;
  numero: string;
  statut: string;
  titre: string;
}

interface RecentChat {
  id: string;
  numero: string;
  statut: string;
  typeDemande: string;
  titre: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<TypeDemande, string> = {
  information: "Information / Question générale",
  installation: "Aide pour une installation",
  sav: "Question machine / SAV",
};

const STATUT_LABELS: Record<string, string> = {
  nouveau: "Nouveau",
  ouvert: "Ouvert",
  attente_revendeur: "En attente",
  escalade_pudu: "Escaladé Pudu",
  ferme: "Fermé",
};

const STATUT_COLORS: Record<string, string> = {
  nouveau: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  ouvert: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  attente_revendeur: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  escalade_pudu: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  ferme: "bg-secondary text-muted-foreground",
};

const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/heic",
  "video/mp4", "video/quicktime", "video/webm", "video/avi",
  "application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function getTypeMedia(mime: string): "image" | "video" | "document" {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "document";
}

// ─── Empty state form ─────────────────────────────────────────────────────────

interface FormState {
  titre: string;
  description: string;
  clientFinalNom: string;
  clientFinalAdresse: string;
  dateInstallation: string;
  robotSn: string;
  robotId: string | null;
  robotType: string | null;
  robotClientNom: string | null;
  problemeConstate: string;
  files: File[];
}

const EMPTY_FORM: FormState = {
  titre: "",
  description: "",
  clientFinalNom: "",
  clientFinalAdresse: "",
  dateInstallation: "",
  robotSn: "",
  robotId: null,
  robotType: null,
  robotClientNom: null,
  problemeConstate: "",
  files: [],
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChatsPage() {
  const { portailUser } = usePortailAuth();
  const { navigate } = usePortailRouter();

  const [chats, setChats] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const listPollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchChats = useCallback(() => {
    authFetch("/api/portail/chats")
      .then(r => r.json())
      .then(d => setChats(d.chats || []))
      .catch(() => {});
  }, []);

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    authFetch("/api/portail/chats")
      .then(r => r.json())
      .then(d => setChats(d.chats || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll every 15s
  useEffect(() => {
    if (listPollTimer.current) clearInterval(listPollTimer.current);
    listPollTimer.current = setInterval(fetchChats, 15_000);
    return () => { if (listPollTimer.current) clearInterval(listPollTimer.current); };
  }, [fetchChats]);

  // Refetch on window focus
  useEffect(() => {
    window.addEventListener("focus", fetchChats);
    return () => window.removeEventListener("focus", fetchChats);
  }, [fetchChats]);

  // Tab title with unread count
  useEffect(() => {
    const unreadCount = chats.filter((c: any) => c.hasUnread).length;
    document.title = unreadCount > 0
      ? `(${unreadCount}) Chats · Portail`
      : "Chats · Portail";
    return () => { document.title = "Portail Jobotto"; };
  }, [chats]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-primary" />
            Chats SAV
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Échangez directement avec l'équipe Jobotto
          </p>
        </div>
        <button
          data-testid="button-nouveau-chat"
          onClick={() => setModalOpen(true)}
          className="hidden sm:flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouveau chat
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : chats.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <MessageCircle className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="text-foreground font-medium text-lg">Aucun chat ouvert pour le moment</p>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              Démarrez une conversation avec l'équipe Jobotto pour toute question, installation ou problème machine.
            </p>
          </div>
          <button
            data-testid="button-nouveau-chat-empty"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2"
          >
            <Plus className="w-4 h-4" />
            Nouveau chat
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {chats.map((chat: any) => (
            <button
              key={chat.id}
              data-testid={`card-chat-${chat.id}`}
              onClick={() => navigate(`/chats/${chat.id}`)}
              className={`w-full text-left rounded-xl p-4 hover:shadow-sm transition-all border ${
                chat.hasUnread
                  ? "bg-blue-50/60 dark:bg-blue-900/10 border-primary/30 hover:border-primary/50"
                  : "bg-card border-border hover:border-primary/30"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Blue dot for unread */}
                    {chat.hasUnread && (
                      <span className="w-2 h-2 rounded-full bg-primary shrink-0 flex-none" aria-label="Nouveau message" />
                    )}
                    <span className={`font-mono text-xs ${chat.hasUnread ? "font-bold text-foreground" : "text-muted-foreground"}`}>
                      {chat.numero}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[chat.statut] || "bg-secondary text-muted-foreground"}`}>
                      {STATUT_LABELS[chat.statut] || chat.statut}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                      {TYPE_LABELS[chat.typeDemande as TypeDemande] || chat.typeDemande}
                    </span>
                  </div>
                  <p className={`mt-1 truncate ${chat.hasUnread ? "font-semibold text-foreground" : "font-medium text-foreground"}`}>
                    {chat.titre}
                  </p>
                  {chat.robotSn && (
                    <p className="text-xs text-muted-foreground mt-0.5">SN : {chat.robotSn}</p>
                  )}
                  {chat.hasUnread && (
                    <p className="text-xs text-primary font-medium mt-1">Nouvelle réponse Jobotto</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {chat.hasUnread && (
                    <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
                  )}
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* FAB — mobile only */}
      <button
        data-testid="button-fab-nouveau-chat"
        onClick={() => setModalOpen(true)}
        className="sm:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all"
        aria-label="Nouveau chat"
      >
        <Plus className="w-6 h-6" />
      </button>

      {modalOpen && (
        <NewChatModal
          onClose={() => setModalOpen(false)}
          onSuccess={(id) => {
            setModalOpen(false);
            navigate(`/chats/${id}`);
          }}
        />
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function NewChatModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: (id: string) => void }) {
  const [step, setStep] = useState<1 | 2>(1);
  const [type, setType] = useState<TypeDemande | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SN autocomplete
  const [snQuery, setSnQuery] = useState("");
  const [snResults, setSnResults] = useState<PuduRobotMatch[]>([]);
  const [snLoading, setSnLoading] = useState(false);
  const [snDropOpen, setSnDropOpen] = useState(false);
  const snTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Doublon
  const [doublon, setDoublon] = useState<DoublonChat | null>(null);
  const [forceCreate, setForceCreate] = useState(false);

  // Recent chats for this SN
  const [recentChats, setRecentChats] = useState<RecentChat[]>([]);

  // File drag
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSnSearch = (q: string) => {
    setSnQuery(q);
    setForm(f => ({ ...f, robotSn: q, robotId: null, robotType: null, robotClientNom: null }));
    setDoublon(null);
    setForceCreate(false);
    setRecentChats([]);

    if (snTimer.current) clearTimeout(snTimer.current);
    if (!q.trim()) { setSnResults([]); setSnDropOpen(false); return; }

    snTimer.current = setTimeout(async () => {
      setSnLoading(true);
      try {
        const r = await authFetch(`/api/portail/robots/search?q=${encodeURIComponent(q)}`);
        const d = await r.json();
        setSnResults(d.robots || []);
        setSnDropOpen(true);
      } catch { setSnResults([]); } finally { setSnLoading(false); }
    }, 300);
  };

  const handleSnSelect = async (robot: PuduRobotMatch) => {
    setSnQuery(robot.robotSn);
    setForm(f => ({
      ...f,
      robotSn: robot.robotSn,
      robotId: robot.id,
      robotType: robot.robotType,
      robotClientNom: robot.clientNom || null,
      clientFinalNom: f.clientFinalNom || robot.clientNom || "",
    }));
    setSnResults([]);
    setSnDropOpen(false);

    // Check doublon + recent chats
    try {
      const r = await authFetch(`/api/portail/chats/check-doublon-sn?sn=${encodeURIComponent(robot.robotSn)}`);
      const d = await r.json();
      if (d.doublon) setDoublon(d.doublon);
      if (d.recentChats) setRecentChats(d.recentChats);
    } catch {}
  };

  const handleSnBlur = async () => {
    setTimeout(() => setSnDropOpen(false), 150);
    if (form.robotSn && !form.robotId) {
      // Free-text SN — still check doublon
      try {
        const r = await authFetch(`/api/portail/chats/check-doublon-sn?sn=${encodeURIComponent(form.robotSn)}`);
        const d = await r.json();
        if (d.doublon) setDoublon(d.doublon);
        if (d.recentChats) setRecentChats(d.recentChats);
      } catch {}
    }
  };

  const handleFiles = (incoming: File[]) => {
    const valid = incoming.filter(f => ALLOWED_TYPES.includes(f.type));
    const totalSize = [...form.files, ...valid].reduce((s, f) => s + f.size, 0);
    const combined = [...form.files, ...valid].slice(0, 10);
    if (totalSize > 50 * 1024 * 1024) {
      setError("Taille totale des fichiers dépasse 50 Mo.");
      return;
    }
    setForm(f => ({ ...f, files: combined }));
    setError(null);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  }, [form.files]);

  const removeFile = (idx: number) => {
    setForm(f => ({ ...f, files: f.files.filter((_, i) => i !== idx) }));
  };

  const isFormValid = () => {
    if (!type) return false;
    if (!form.titre.trim()) return false;
    if (type === "information" && !form.description.trim()) return false;
    if (type === "installation") {
      if (!form.clientFinalNom.trim() || !form.description.trim()) return false;
    }
    if (type === "sav") {
      if (!form.robotSn.trim() || !form.clientFinalNom.trim() || !form.titre.trim() || !form.problemeConstate.trim()) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!type || !isFormValid()) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("typeDemande", type);
      fd.append("titre", form.titre);
      if (form.description) fd.append("description", form.description);
      if (form.clientFinalNom) fd.append("clientFinalNom", form.clientFinalNom);
      if (form.clientFinalAdresse) fd.append("clientFinalAdresse", form.clientFinalAdresse);
      if (form.dateInstallation) fd.append("dateInstallation", form.dateInstallation);
      if (form.robotSn) fd.append("robotSn", form.robotSn);
      if (form.robotId) fd.append("robotId", form.robotId);
      if (form.problemeConstate) fd.append("problemeConstate", form.problemeConstate);
      if (forceCreate) fd.append("forceCreate", "true");
      form.files.forEach(f => fd.append("attachments", f));

      const res = await authFetch("/api/portail/chats", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) {
        if (data.doublon) {
          setDoublon(data.doublon);
          setStep(2);
          return;
        }
        throw new Error(data.message || "Erreur lors de la création du chat");
      }
      onSuccess(data.id);
    } catch (e: any) {
      setError(e.message || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <h2 className="font-semibold text-foreground">Nouveau chat</h2>
              <p className="text-xs text-muted-foreground">Étape {step} / 2</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 1 ? (
            <Step1TypeSelector selected={type} onSelect={t => { setType(t); setStep(2); }} />
          ) : type === "information" ? (
            <StepInformation form={form} setForm={setForm} isDragging={isDragging} setIsDragging={setIsDragging} handleDrop={handleDrop} handleFiles={handleFiles} removeFile={removeFile} fileInputRef={fileInputRef} />
          ) : type === "installation" ? (
            <StepInstallation form={form} setForm={setForm} isDragging={isDragging} setIsDragging={setIsDragging} handleDrop={handleDrop} handleFiles={handleFiles} removeFile={removeFile} fileInputRef={fileInputRef} />
          ) : (
            <StepSav
              form={form} setForm={setForm}
              snQuery={snQuery} snResults={snResults} snLoading={snLoading} snDropOpen={snDropOpen}
              onSnSearch={handleSnSearch} onSnSelect={handleSnSelect} onSnBlur={handleSnBlur}
              doublon={doublon} forceCreate={forceCreate} setForceCreate={setForceCreate}
              recentChats={recentChats}
              isDragging={isDragging} setIsDragging={setIsDragging} handleDrop={handleDrop} handleFiles={handleFiles} removeFile={removeFile} fileInputRef={fileInputRef}
            />
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="px-6 py-4 border-t border-border shrink-0">
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 mb-3">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}
            <button
              data-testid="button-submit-chat"
              onClick={handleSubmit}
              disabled={!isFormValid() || submitting}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Ouvrir le chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Type Selector ────────────────────────────────────────────────────

function Step1TypeSelector({ selected, onSelect }: { selected: TypeDemande | null; onSelect: (t: TypeDemande) => void }) {
  const TYPES: { key: TypeDemande; label: string; desc: string; icon: React.ReactNode; color: string }[] = [
    {
      key: "information",
      label: "Information / Question générale",
      desc: "Pour toute demande qui n'est pas liée à une machine",
      icon: <PhoneCall className="w-6 h-6" />,
      color: "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20",
    },
    {
      key: "installation",
      label: "Aide pour une installation",
      desc: "Accompagnement à distance sur une mise en service",
      icon: <Wrench className="w-6 h-6" />,
      color: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20",
    },
    {
      key: "sav",
      label: "Question machine / SAV",
      desc: "Problème constaté sur un robot",
      icon: <Bot className="w-6 h-6" />,
      color: "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20",
    },
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground mb-4">Quel type de demande souhaitez-vous ouvrir ?</p>
      {TYPES.map(t => (
        <button
          key={t.key}
          data-testid={`card-type-${t.key}`}
          onClick={() => onSelect(t.key)}
          className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all hover:shadow-sm
            ${selected === t.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 bg-card"}`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${t.color}`}>
            {t.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground">{t.label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t.desc}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      ))}
    </div>
  );
}

// ─── Shared components ─────────────────────────────────────────────────────────

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength, "data-testid": tid }: {
  value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number; "data-testid"?: string;
}) {
  return (
    <input
      data-testid={tid}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-3 py-2 bg-input border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 4, "data-testid": tid }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; "data-testid"?: string;
}) {
  return (
    <textarea
      data-testid={tid}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 bg-input border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
    />
  );
}

function FileZone({ isDragging, setIsDragging, handleDrop, handleFiles, removeFile, files, fileInputRef, helper }: {
  isDragging: boolean; setIsDragging: (v: boolean) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleFiles: (f: File[]) => void;
  removeFile: (idx: number) => void;
  files: File[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  helper?: string;
}) {
  return (
    <div className="space-y-2">
      {helper && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />
          <span>{helper}</span>
        </div>
      )}
      <div
        data-testid="dropzone-attachments"
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
          ${isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-secondary/30"}`}
      >
        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Glissez vos fichiers ici ou <span className="text-primary font-medium">cliquez pour sélectionner</span>
        </p>
        <p className="text-xs text-muted-foreground mt-1">Max 10 fichiers · 50 Mo au total</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_TYPES.join(",")}
          className="hidden"
          onChange={e => handleFiles(Array.from(e.target.files || []))}
        />
      </div>
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f, i) => (
            <div key={i} data-testid={`file-item-${i}`} className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-lg text-sm">
              <FileIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0 truncate text-foreground">{f.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
              <button onClick={() => removeFile(i)} className="p-0.5 text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Information ───────────────────────────────────────────────────────

function StepInformation({ form, setForm, ...fileProps }: any) {
  return (
    <div className="space-y-4">
      <Field label="Titre court" required>
        <TextInput
          data-testid="input-titre"
          value={form.titre}
          onChange={v => setForm((f: FormState) => ({ ...f, titre: v }))}
          placeholder="Décrivez en quelques mots…"
          maxLength={200}
        />
      </Field>
      <Field label="Description initiale" required>
        <TextArea
          data-testid="input-description"
          value={form.description}
          onChange={v => setForm((f: FormState) => ({ ...f, description: v }))}
          placeholder="Détaillez votre question ou demande…"
        />
      </Field>
      <Field label="Pièces jointes (optionnel)">
        <FileZone files={form.files} {...fileProps} fileInputRef={fileProps.fileInputRef} />
      </Field>
    </div>
  );
}

// ─── Step 2: Installation ──────────────────────────────────────────────────────

function StepInstallation({ form, setForm, ...fileProps }: any) {
  return (
    <div className="space-y-4">
      <Field label="Titre court" required>
        <TextInput
          data-testid="input-titre"
          value={form.titre}
          onChange={v => setForm((f: FormState) => ({ ...f, titre: v }))}
          placeholder="Ex : Installation BellaMatic chez le client X"
          maxLength={200}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nom du client final" required>
          <TextInput
            data-testid="input-client-final-nom"
            value={form.clientFinalNom}
            onChange={v => setForm((f: FormState) => ({ ...f, clientFinalNom: v }))}
            placeholder="Société ou nom"
          />
        </Field>
        <Field label="Date d'installation prévue">
          <input
            data-testid="input-date-installation"
            type="date"
            value={form.dateInstallation}
            onChange={e => setForm((f: FormState) => ({ ...f, dateInstallation: e.target.value }))}
            className="w-full px-3 py-2 bg-input border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>
      </div>
      <Field label="Adresse d'installation">
        <TextInput
          value={form.clientFinalAdresse}
          onChange={v => setForm((f: FormState) => ({ ...f, clientFinalAdresse: v }))}
          placeholder="Adresse complète"
        />
      </Field>
      <Field label="Description / contexte" required>
        <TextArea
          data-testid="input-description"
          value={form.description}
          onChange={v => setForm((f: FormState) => ({ ...f, description: v }))}
          placeholder="Décrivez le contexte de l'installation, le modèle de robot, les spécificités…"
        />
      </Field>
      <Field label="Pièces jointes (optionnel)">
        <FileZone files={form.files} {...fileProps} fileInputRef={fileProps.fileInputRef} />
      </Field>
    </div>
  );
}

// ─── Step 2: SAV ──────────────────────────────────────────────────────────────

function StepSav({ form, setForm, snQuery, snResults, snLoading, snDropOpen, onSnSearch, onSnSelect, onSnBlur, doublon, forceCreate, setForceCreate, recentChats, ...fileProps }: any) {
  const { navigate } = usePortailRouter();

  return (
    <div className="space-y-4">
      {/* SN */}
      <Field label="Numéro de série (SN)" required>
        <div className="relative">
          <input
            data-testid="input-robot-sn"
            value={snQuery}
            onChange={e => onSnSearch(e.target.value)}
            onBlur={onSnBlur}
            placeholder="Ex : PD-CC-24001234"
            className="w-full px-3 py-2 bg-input border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring font-mono"
          />
          {snLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground absolute right-3 top-2.5" />}
          {snDropOpen && snResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
              {snResults.map((r: PuduRobotMatch) => (
                <button
                  key={r.id}
                  data-testid={`sn-result-${r.robotSn}`}
                  onMouseDown={() => onSnSelect(r)}
                  className="w-full px-3 py-2.5 text-left hover:bg-secondary/60 transition-colors"
                >
                  <span className="font-mono text-sm font-medium text-foreground">{r.robotSn}</span>
                  {r.robotType && <span className="ml-2 text-xs text-muted-foreground">{r.robotType}</span>}
                  {r.clientNom && <span className="ml-2 text-xs text-muted-foreground">· {r.clientNom}</span>}
                </button>
              ))}
            </div>
          )}
          {snDropOpen && snResults.length === 0 && snQuery && !snLoading && (
            <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg px-3 py-2.5 text-sm text-muted-foreground">
              SN non trouvé — vous pouvez tout de même ouvrir le chat.
            </div>
          )}
        </div>
        {/* Robot info si trouvé */}
        {form.robotId && (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-xs text-green-700 dark:text-green-400 mt-1">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            <span>Robot reconnu : {form.robotType || "—"}{form.robotClientNom ? ` · ${form.robotClientNom}` : ""}</span>
          </div>
        )}
      </Field>

      {/* Doublon warning */}
      {doublon && !forceCreate && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-4">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Un chat est déjà ouvert pour ce robot :{" "}
              <span className="font-mono font-semibold">{doublon.numero}</span> — Souhaitez-vous l'ouvrir plutôt que d'en créer un nouveau ?
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="button-open-existing-chat"
              onClick={() => navigate(`/chats/${doublon.id}`)}
              className="flex-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
            >
              Ouvrir le chat existant
            </button>
            <button
              data-testid="button-force-create-chat"
              onClick={() => setForceCreate(true)}
              className="flex-1 px-3 py-1.5 border border-amber-400 text-amber-700 dark:text-amber-400 rounded-lg text-sm hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            >
              Créer quand même
            </button>
          </div>
        </div>
      )}

      {/* Recent chats for this SN */}
      {recentChats.length > 0 && (
        <div className="rounded-xl border border-border bg-secondary/30 p-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Historique pour ce SN :</p>
          <div className="space-y-1.5">
            {recentChats.slice(0, 3).map((c: RecentChat) => (
              <div key={c.id} className="flex items-center justify-between text-xs">
                <span className="font-mono text-muted-foreground">{c.numero}</span>
                <span className={`px-2 py-0.5 rounded-full ${STATUT_COLORS[c.statut] || "bg-secondary text-muted-foreground"}`}>
                  {STATUT_LABELS[c.statut] || c.statut}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Nom du client final" required>
          <TextInput
            data-testid="input-client-final-nom"
            value={form.clientFinalNom}
            onChange={v => setForm((f: FormState) => ({ ...f, clientFinalNom: v }))}
            placeholder="Nom ou société"
          />
        </Field>
        <Field label="Adresse du client final">
          <TextInput
            value={form.clientFinalAdresse}
            onChange={v => setForm((f: FormState) => ({ ...f, clientFinalAdresse: v }))}
            placeholder="Adresse"
          />
        </Field>
      </div>

      <Field label="Titre court" required>
        <TextInput
          data-testid="input-titre"
          value={form.titre}
          onChange={v => setForm((f: FormState) => ({ ...f, titre: v }))}
          placeholder="Ex : Robot bloqué en mode recharge"
          maxLength={200}
        />
      </Field>

      <Field label="Description détaillée du problème constaté" required>
        <TextArea
          data-testid="input-probleme"
          value={form.problemeConstate}
          onChange={v => setForm((f: FormState) => ({ ...f, problemeConstate: v }))}
          placeholder="Décrivez précisément le problème, les conditions d'apparition, les messages d'erreur…"
          rows={5}
        />
      </Field>

      <Field label="Pièces jointes">
        <FileZone
          files={form.files}
          {...fileProps}
          fileInputRef={fileProps.fileInputRef}
          helper="Une photo et/ou une vidéo accélèrent considérablement le diagnostic."
        />
      </Field>
    </div>
  );
}
